import React, { useState, useRef, useEffect, useMemo } from "react";
import "./OrdersPage.css";
import KdsTabsCollapse from "./KdsTabsCollapse";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { useLocation } from "react-router-dom";
import KitchenDisplaySystem from "../KDS";
import DeliveryV2 from "../delivery/indexV2";
import OrdersTable from "../product/all-products/OrdersTable";
import Returns from "../Returns/Returns";
import { PosCustomButton } from "../../../customComponents/PosCustomButton/PosCustomButton";
import PosCustomIcon from "../../../customComponents/PosCustomIcon/PosCustomIcon";
import { ArrowPathIcon, PauseCircleIcon, PlayCircleIcon } from "@heroicons/react/24/outline";
import { IconButton, Tooltip, Box, Modal } from "@mui/material";
import {
  fetchConsumedDays,
  getBranchDetails,
  pauseOnlineOrderRestaurantV2,
  getAllPrepStations,
} from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import PosCustomCheckBox from "../../../customComponents/PosCustomCheckBox/PosCustomCheckBox";
import PosCustomFilterV3 from "../../../customComponents/PosCustomFilterV3/PosCustomFilterV3";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import OdsDateFilter from "../delivery/components/OdsDateFilter/OdsDateFilter";
import PosCustomDateFilter from "../../../customComponents/PosCustomDateFilter/PosCustomDateFilter";
import StyledSelect from "../product/all-products/components/custom/StyledSelect";
import { useDispatch } from "react-redux";
import { customEventTrigger } from "../../../analytics";
import {
  odsFilter,
  orderFilter,
  returnFilter,
  setOrderAggTab,
} from "../product/all-products/redux/Actions/paginationActions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subscribeSlots } from "../../../utils/odsArchitecture/odsSlotService";

const ReturnsDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className="returns-filter-date-btn"
    data-testid="button-returns-filter-date"
    type="button"
  >
    <span className="returns-filter-date-btn-span">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lucide lucide-calendar-days"
        style={{ height: "16px", width: "16px", color: "#94a3b8" }}
      >
        <path d="M8 2v4"></path>
        <path d="M16 2v4"></path>
        <rect width="18" height="18" x="3" y="4" rx="2"></rect>
        <path d="M3 10h18"></path>
        <path d="M8 14h.01"></path>
        <path d="M12 14h.01"></path>
        <path d="M16 14h.01"></path>
        <path d="M8 18h.01"></path>
        <path d="M12 18h.01"></path>
        <path d="M16 18h.01"></path>
      </svg>
      {value || "Select Date"}
    </span>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lucide lucide-chevron-down"
      style={{ height: "16px", width: "16px", color: "#94a3b8" }}
    >
      <path d="m6 9 6 6 6-6"></path>
    </svg>
  </button>
));

const OrdersPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "Online Orders");
  const [kdsTabsCollapsed, setKdsTabsCollapsed] = useState(true);
  const isKdsTab = activeTab === "Kitchen Display (KDS)";
  useEffect(() => {
    if (!isKdsTab) setKdsTabsCollapsed(true);
  }, [isKdsTab]);
  const [historyDate, setHistoryDate] = useState(null);
  const dispatch = useDispatch();

  const appConfigData = localStorage.getItem("appConfig");
  const appConfig = appConfigData ? JSON.parse(localStorage.getItem("appConfig")) : {};
  const enabledDisplayModes = appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens;
  const isOdsEnabled = enabledDisplayModes?.find((mode) => mode === "ODS");
  const showOdsTab = JSON.parse(localStorage.getItem("orgConfig"))?.odsScreen || false;
  const isOdsUser = localStorage.getItem("user_roles")?.includes("ODS_USER") || false;

  const handleHistoryDateSelect = (e) => {
    customEventTrigger("pos_orderTable_date_selected", {
      date_value: e,
    });
    setHistoryDate(e);
    dispatch(orderFilter(""));
  };

  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [platformSyncStatus, setPlatformSyncStatus] = useState({});
  const enabledPlatformsCount = activePlatforms?.length || 0;
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [pauseOrderLoader, setPauseOrderLoader] = useState(null);
  const [odsDate, setOdsDate] = useState(null);
  const [hasFutureOrders, setHasFutureOrders] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [prepStations, setPrepStations] = useState([]);
  const [selectedPrepStations, setSelectedPrepStations] = useState([]);
  const [returnsFilter, setReturnsFilter] = useState({
    value: "Session orders",
    label: "Session orders",
  });
  const [returnsDate, setReturnsDate] = useState(null);

  const showSnackbar = useSnackbar();
  const deliveryRef = useRef();

  const retailType = localStorage.getItem("retailType");
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");

  const [orderValue, setOrderValue] = useState(
    location.state?.openCreditSummary
      ? { value: "Credit orders", label: "Credit orders" }
      : { value: "Session orders", label: "Session orders" }
  );

  const orderSelectOptions = useMemo(() => {
    const baseOptions = [
      { value: "All orders", label: "All orders" },
      { value: "Cashier orders", label: "Cashier orders" },
      { value: "Session orders", label: "Session orders" },
      { value: "App orders", label: "App orders" },
      { value: "In-store Delivery", label: "In-store Delivery" },
      { value: "Credit Orders Listing", label: "Credit Orders" },
    ];

    if (retailType === "RESTAURANT") {
      baseOptions.push({ value: "Swiggy/Zomato", label: "Swiggy Zomato Orders" });
    }

    return baseOptions;
  }, [retailType]);

  const handleSelectChange = (e) => {
    customEventTrigger("pos_ordersTable_dropdown", {
      items_value: e.value,
    });
    setOrderValue(e);
  };

  const returnsFilterOptions = useMemo(() => {
    const baseOptions = [
      { value: "All orders", label: "All Orders" },
      { value: "Cashier orders", label: "Cashier Orders" },
      { value: "Session orders", label: "Session Orders" },
      { value: "App orders", label: "App Orders" },
      { value: "In-store Delivery", label: "In-store Delivery" },
      { value: "Credit Orders Listing", label: "Credit Orders" },
    ];

    if (retailType === "RESTAURANT") {
      baseOptions.push({ value: "Swiggy/Zomato", label: "Swiggy/Zomato Orders" });
    }

    return baseOptions;
  }, [retailType]);

  const pauseModalStyle = {
    backgroundColor: "#fff",
    color: "black",
    position: "fixed",
    top: 0,
    right: 0,
    width: "100%",
    maxWidth: "520px",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.18)",
    borderLeft: "1px solid rgba(226, 232, 240, 0.8)",
    outline: "none",
  };

  const tabs = [
    {
      label: "Online Orders",
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      ),
    },
    ...(retailType === "RESTAURANT"
      ? [
          {
            label: "Kitchen Display (KDS)",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z" />
                <path d="M6 17h12" />
              </svg>
            ),
          },
        ]
      : []),
    {
      label: "Order History",
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l4 2" />
        </svg>
      ),
    },
    {
      label: "Returns",
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M9 14 4 9l5-5" />
          <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
        </svg>
      ),
    },
  ];

  const platformOptions = [
    {
      value: "zomato",
      label: "Zomato",
      icon: <div className="platform-icon-letter platform-zomato">Z</div>,
    },
    {
      value: "swiggy",
      label: "Swiggy",
      icon: <div className="platform-icon-letter platform-swiggy">S</div>,
    },
    {
      value: "ownly",
      label: "Ownly",
      icon: <div className="platform-icon-letter platform-swiggy">O</div>,
    },
    {
      value: "ubereats",
      label: "UberEats",
      icon: <div className="platform-icon-letter platform-ubereats">U</div>,
    },
    {
      value: "website",
      label: "Website",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="platform-svg platform-website"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      ),
    },
    {
      value: "whatsapp",
      label: "WhatsApp",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="platform-svg platform-whatsapp"
          aria-hidden="true"
        >
          <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
        </svg>
      ),
    },
    {
      value: "magicpin",
      label: "MagicPin",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="platform-svg platform-magicpin"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      ),
    },
  ];

  const {
    isLoading: isLoadingConsumedDays,
    data: consumedDaysData,
    mutate: getConsumedDays,
  } = useMutation({
    mutationKey: ["consumed-days"],
    mutationFn: async (payload) => {
      const response = await fetchConsumedDays(payload);

      if (response?.data?.code === "ECONNRESET") {
        throw new Error(response?.data?.message);
      }

      if (!!response?.data?.data?.es) {
        throw new Error(response?.data?.data?.message);
      }

      return response?.data?.data?.dataList?.map((item) => item?.date);
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const renderDayContents = (day, date) => {
    const isConsumed = consumedDaysData?.some((consumedDate) => {
      const d = new Date(consumedDate);

      return (
        d.getDate() === date.getDate() &&
        d.getMonth() === date.getMonth() &&
        d.getFullYear() === date.getFullYear()
      );
    });

    return (
      <div className="day-contents">
        {isConsumed && <div className="indicator-dot"></div>}
        <span>{day}</span>
      </div>
    );
  };

  const handleMonthChange = (date = new Date()) => {
    const monthStart = moment(date).startOf("month").format("YYYY-MM-DD");
    const monthEnd = moment(date).endOf("month").format("YYYY-MM-DD");

    getConsumedDays({
      slotStartDate: monthStart,
      slotEndDate: monthEnd,
      locationId: locId,
    });
  };

  const handleTabClick = (tab) => {
    if (tab === "Kitchen Display (KDS)" && !enabledDisplayModes?.includes("KDS")) {
      showSnackbar("KDS is disabled from the RMS", "warning");
      return;
    }

    if (tab === "Online Orders") {
      const odsEnabled = retailType === "RESTAURANT" ? isOdsEnabled : showOdsTab;

      if (!odsEnabled) {
        showSnackbar("ODS is disabled from configuration", "warning");
        return;
      }

      if (!isOdsUser) {
        showSnackbar("You are not authorized", "error");
        return;
      }
    }

    setActiveTab(tab);
  };

  // Publish the active tab so the global search (in the top navbar) knows when
  // "Order History" or "Returns" is on screen and can switch from "coming soon"
  // to a working search. Clear each filter whenever we leave its tab so it
  // doesn't leak into other tabs/pages.
  useEffect(() => {
    dispatch(setOrderAggTab(activeTab));
    if (activeTab !== "Order History") {
      dispatch(orderFilter(""));
    }
    if (activeTab !== "Returns") {
      dispatch(returnFilter(""));
    }
    if (activeTab !== "Online Orders") {
      dispatch(odsFilter(""));
    }
    return () => {
      dispatch(setOrderAggTab(""));
      dispatch(orderFilter(""));
      dispatch(returnFilter(""));
      dispatch(odsFilter(""));
    };
  }, [activeTab]);

  const fetchPlatformStatus = () => {
    getBranchDetails(locId)
      .then((res) => {
        const platformStatus = res?.data?.branch?.storeTogglePlatformStatus || {};
        const syncStatus = res?.data?.branch?.platformStatusSync || {};

        const enabledPlatforms = Object.keys(platformStatus)
          .filter((p) => platformStatus[p] === "ENABLED")
          .map((p) => p.toLowerCase());

        const formattedPlatforms = Object.keys(platformStatus)?.map((p) => p?.toLowerCase());
        const formattedSyncStatus = {};
        Object.keys(syncStatus)?.forEach((key) => {
          formattedSyncStatus[key.toLowerCase()] = syncStatus[key];
        });

        setAvailablePlatforms(formattedPlatforms);
        setSelectedPlatforms(enabledPlatforms);
        setActivePlatforms(enabledPlatforms);
        setPlatformSyncStatus(formattedSyncStatus);
      })
      .catch((err) => {});
  };

  const handlePauseOrder = () => {
    fetchPlatformStatus();
    setIsPlatformModalOpen(true);
  };

  const updatePlatformStatus = (enabledPlatforms = [], actionType) => {
    setPauseOrderLoader(actionType);

    const storeTogglePlatformStatus = {};

    platformOptions
      ?.filter((p) => availablePlatforms?.includes(p?.value))
      ?.forEach((platform) => {
        storeTogglePlatformStatus[platform?.value] = enabledPlatforms?.includes(platform?.value)
          ? "ENABLED"
          : "DISABLED";
      });

    const payload = {
      location_ref_id: locId,
      storeTogglePlatformStatus,
      turn_on_at: null,
    };

    pauseOnlineOrderRestaurantV2(payload)
      .then((res) => {
        if (res?.data?.data?.es > 0) {
          showSnackbar(res?.data?.data?.message || "Unable to enable all platforms", "error");
          setPauseOrderLoader(null);
          setIsPlatformModalOpen(false);
          return;
        }
        setActivePlatforms(enabledPlatforms);
        showSnackbar("All platforms enabled successfully", "success");
        setPauseOrderLoader(null);
        setIsPlatformModalOpen(false);
      })
      .catch((err) => {
        showSnackbar(err?.message || "Unable to enable all platforms", "error");
        setPauseOrderLoader(null);
      });
  };

  const handlePlatformCheck = (e) => {
    const { value, checked } = e.target;
    if (!availablePlatforms?.includes(value)) return;

    setSelectedPlatforms((prev) => (checked ? [...prev, value] : prev?.filter((v) => v !== value)));
  };

  const isAllPaused = availablePlatforms?.length > 0 && selectedPlatforms?.length === 0;

  const handlePauseAllToggle = () => {
    if (isAllPaused) {
      setSelectedPlatforms([...availablePlatforms]);
    } else {
      setSelectedPlatforms([]);
    }
  };

  const handlePlatformToggle = (platformValue) => {
    if (!availablePlatforms?.includes(platformValue)) return;

    setSelectedPlatforms((prev) =>
      prev?.includes(platformValue)
        ? prev?.filter((v) => v !== platformValue)
        : [...prev, platformValue]
    );
  };

  const handleApplyPlatforms = () => {
    updatePlatformStatus(selectedPlatforms, "apply");
  };

  useEffect(() => {
    fetchPlatformStatus();
    const interval = setInterval(fetchPlatformStatus, 30000);
    return () => clearInterval(interval);
  }, []);



  // Fetch prep stations
  useEffect(() => {
    const fetchPrepStations = async () => {
      try {
        const payload = {
          page: 1,
          pageSize: 100,
          sortByUpdatedAt: "DESC",
          storeLocationId: [locId],
          storeIds: [orgId]
        };
        const res = await getAllPrepStations(payload);
        const stations = res?.data?.data?.data?.data || [];

        setPrepStations(stations);
      } catch (error) {
        setPrepStations([]);
      }
    };

    if (locId && retailType === "RESTAURANT") {
      fetchPrepStations();
    }
  }, [locId, retailType]);

  return (
    <Navbar>
      <div className="orderAggsContainer">
        <div className={`orderAggsHeader${isKdsTab ? " orderAggsHeader--kds" : ""}${isKdsTab && kdsTabsCollapsed ? " orderAggsHeader--tabs-collapsed" : ""}`}>
          <KdsTabsCollapse enabled={isKdsTab} collapsed={kdsTabsCollapsed} onToggle={() => setKdsTabsCollapsed((value) => !value)}>
          <div className="aggTabsContainer">
            {tabs?.map((tab, idx) => (
              <div
                key={idx}
                className={`aggsTabsInnerContainer ${activeTab === tab?.label ? "active" : ""}`}
                hidden={isKdsTab && kdsTabsCollapsed && activeTab !== tab?.label}
                onClick={() => handleTabClick(tab?.label)}
              >
                <span className="tab-icon">{tab?.icon}</span>
                <p className="textAlignCorrection">{tab?.label}</p>
              </div>
            ))}
          </div>
          </KdsTabsCollapse>

          {/* ODS */}
          {activeTab === "Online Orders" && (
            <div className="orderAggsHeaderRightContainer">
              <div className={`board-status-date-container`}>
                <OdsDateFilter
                  selectedDate={odsDate}
                  onChange={(d) => setOdsDate(d)}
                  renderDayContents={renderDayContents}
                  onMonthChange={handleMonthChange}
                  onCalendarOpen={handleMonthChange}
                />
                {hasFutureOrders && <span className="future-orders-dot" />}
              </div>
              <div
                className="board-status-refresh-container"
                onClick={() => {
                  deliveryRef.current?.refreshOrders();
                }}
              >
                <Tooltip key={"REFRESH"} title={"Refresh"} placement="bottom">
                  <IconButton>
                    <PosCustomIcon
                      icon={<ArrowPathIcon />}
                      height={"16px"}
                      width={"16px"}
                      color="var(--blue-b0)"
                    />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="pause-orders-btn-wrapper">
                <PosCustomButton
                  variant="red-sand"
                  radius="50px"
                  p="8px 16px"
                  title="Pause Orders"
                  iconOnLeft={
                    <PosCustomIcon
                      icon={<PauseCircleIcon />}
                      height={"20px"}
                      width={"20px"}
                      color="#F54900"
                    />
                  }
                  onClickFunction={handlePauseOrder}
                  shadow={
                    "0 -1px 3px rgba(0, 0, 0, 0.04), 0 -1px 2px rgba(0, 0, 0, 0.02),0 1px 3px rgba(0, 0, 0, 0.04),0 1px 2px rgba(0, 0, 0, 0.02)"
                  }
                />
                {enabledPlatformsCount > 0 && (
                  <span className="pause-orders-count-badge">{enabledPlatformsCount}</span>
                )}
              </div>
            </div>
          )}

          {activeTab === "Kitchen Display (KDS)" && (
            <div className="orderAggsHeaderRightContainer orderAggsHeaderRightContainer--kds">
              <div id="kds-slots-header-mount" aria-label="Kitchen order delivery slots" />
              <div className="prepstation-filter-wrapper">
                <PosCustomFilterV3
                  options={prepStations?.map((station) => ({
                    value: station?.id,
                    label: station?.displayName,
                  }))}
                  selectedValues={selectedPrepStations}
                  onChange={(filter) => setSelectedPrepStations(filter)}
                  styleProps={{
                    buttonLabel: "Prep Station",
                    buttonRadius: "50px",
                    buttonBorder: "none",
                    buttonShadow:
                      "0 -1px 3px rgba(0, 0, 0, 0.04), 0 -1px 2px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
                    dropdownMaxHeight: "320px",
                  }}
                />
              </div>

              <div className="source-filter-wrapper">
                <PosCustomFilterV3
                  options={[
                    { value: "ZOMATO_ORDER", label: "Zomato" },
                    { value: "SWIGGY_ORDER", label: "Swiggy" },
                    { value: ["PALLET_POS", "PALLET_MPOS"], label: "Pos" },
                    { value: "WHATSAPP_ORDER", label: "Whatsapp" },
                    { value: ["B2C_ORDER","RESTAURANT_ORDER"], label: "Website" },
                  ]}
                  selectedValues={selectedSources}
                  onChange={(filter) => setSelectedSources(filter)}
                  styleProps={{
                    buttonLabel: "Filter By Source",
                    buttonRadius: "50px",
                    buttonBorder: "none",
                    buttonShadow:
                      "0 -1px 3px rgba(0, 0, 0, 0.04), 0 -1px 2px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
                  }}
                />
              </div>
              <div className="pause-orders-btn-wrapper">
                <PosCustomButton
                  variant="red-sand"
                  radius="50px"
                  p="6px 16px"
                  title="Pause Orders"
                  iconOnLeft={
                    <PosCustomIcon
                      icon={<PauseCircleIcon />}
                      height={"20px"}
                      width={"20px"}
                      color="#F54900"
                    />
                  }
                  onClickFunction={handlePauseOrder}
                  shadow={
                    "0 -1px 3px rgba(0, 0, 0, 0.04), 0 -1px 2px rgba(0, 0, 0, 0.02),0 1px 3px rgba(0, 0, 0, 0.04),0 1px 2px rgba(0, 0, 0, 0.02)"
                  }
                />
                {enabledPlatformsCount > 0 && (
                  <span className="pause-orders-count-badge">{enabledPlatformsCount}</span>
                )}
              </div>
            </div>
          )}

          {activeTab === "Order History" && (
            <div className="returns-header-container">
              <div className="returns-filters-wrapper">
                <div className="returns-datepicker-wrapper">
                  <PosCustomDateFilter
                    selectedDate={historyDate}
                    onChange={handleHistoryDateSelect}
                  />
                </div>
                <div className="order-history-select-container">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="returns-select-icon-left"
                    aria-hidden="true"
                  >
                    <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path>
                  </svg>
                  <StyledSelect
                    options={orderSelectOptions}
                    value={orderValue}
                    onChange={(opt) => handleSelectChange(opt)}
                    menuPortalTarget={null}
                    isSearchable={false}
                    data-testid="select-history-filter-orders"
                    components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="returns-select-icon-right"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6"></path>
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Returns */}
          {activeTab === "Returns" && (
            <div className="returns-header-container">
              <div className="returns-filters-wrapper">
                <div className="returns-datepicker-wrapper">
                  <DatePicker
                    selected={returnsDate}
                    onChange={(d) => setReturnsDate(d)}
                    customInput={<ReturnsDateInput />}
                    dateFormat="dd/MM/yyyy"
                    isClearable
                  />
                </div>
                <div className="returns-select-container">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="returns-select-icon-left"
                    aria-hidden="true"
                  >
                    <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path>
                  </svg>
                  <StyledSelect
                    options={returnsFilterOptions}
                    value={returnsFilter}
                    onChange={(opt) => setReturnsFilter(opt)}
                    menuPortalTarget={null}
                    isSearchable={false}
                    data-testid="select-returns-filter-orders"
                    components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="returns-select-icon-right"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6"></path>
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* this is the parent tab from which the child is been created */}
        <div className="orderAggsActiveTab">
          {activeTab === "Online Orders" && (
            <DeliveryV2
              ref={deliveryRef}
              odsDate={odsDate}
              setHasFutureOrders={setHasFutureOrders}
            />
          )}
          {activeTab === "Kitchen Display (KDS)" && (
            <KitchenDisplaySystem
              onDisabled={() => setActiveTab("Order History")}
              selectedSources={selectedSources?.flat()}
              selectedPrepStations={selectedPrepStations}
            />
          )}
          {activeTab === "Order History" && (
            <OrdersTable date={historyDate} orderValue={orderValue} setOrderValue={setOrderValue} />
          )}
          {activeTab === "Returns" && <Returns orderValue={returnsFilter} date={returnsDate} />}
        </div>

        <Modal open={isPlatformModalOpen} onClose={() => setIsPlatformModalOpen(false)}>
          <Box sx={pauseModalStyle}>
            <div className="pause-orders-drawer">
              {/* Header */}
              <div className="drawer-header">
                <div>
                  <h2 className="drawer-title">Pause Online Orders</h2>
                </div>
                <button
                  className="drawer-close-btn"
                  data-testid="button-close-pause-orders-drawer"
                  onClick={() => setIsPlatformModalOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-x"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="drawer-content">
                {/* Pause All Banner */}
                <div className="pause-all-banner">
                  <div className="pause-all-flex">
                    <div>
                      <p className="pause-all-title">Pause All Channels</p>
                      <p className="pause-all-subtitle">Stop receiving orders from all sources</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isAllPaused ? "true" : "false"}
                      data-state={isAllPaused ? "checked" : "unchecked"}
                      onClick={handlePauseAllToggle}
                      className="drawer-toggle-switch"
                    >
                      <span
                        data-state={isAllPaused ? "checked" : "unchecked"}
                        className="drawer-toggle-thumb"
                      />
                    </button>
                  </div>
                </div>

                {/* Platforms List */}
                <div className="platforms-list-container">
                  {platformOptions
                    ?.filter((p) => availablePlatforms?.includes(p?.value))
                    ?.map((platform) => {
                      const isEnabled = selectedPlatforms?.includes(platform?.value);
                      const isPending = platformSyncStatus?.[platform?.value] === "PENDING";

                      return (
                        <div key={platform?.value} className="platform-row-item">
                          <div className="platform-info-flex">
                            <div className="platform-logo-box">{platform?.icon}</div>
                            <label
                              className="platform-label"
                              htmlFor={`platform-${platform?.value}`}
                            >
                              {platform?.label}
                            </label>
                          </div>
                          {isPending ? (
                            <span className="platform-waiting-label">Waiting</span>
                          ) : (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isEnabled ? "true" : "false"}
                              data-state={isEnabled ? "checked" : "unchecked"}
                              onClick={() => handlePlatformToggle(platform?.value)}
                              className="drawer-toggle-switch"
                            >
                              <span
                                data-state={isEnabled ? "checked" : "unchecked"}
                                className="drawer-toggle-thumb"
                              />
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="drawer-footer">
                <div className="drawer-footer-actions">
                  <button
                    className="drawer-btn-cancel"
                    data-testid="button-cancel-pause-orders"
                    onClick={() => setIsPlatformModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="drawer-btn-save"
                    data-testid="button-save-pause-orders"
                    onClick={() => {
                      handleApplyPlatforms();
                    }}
                    disabled={pauseOrderLoader === "apply"}
                  >
                    {pauseOrderLoader === "apply" ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </Navbar>
  );
};

export default OrdersPage;
